﻿import { RESTError } from "../../API/RESTError";
import { updateClientConfiguration } from "../../API/Users/Users";
import { MainSite } from "../../MainSite";
import { Legacy, ParameterSets, UserClientConf } from "../../Model/ClientConf/UserClientConf";
import { Station } from "../../Model/Station";
import { ParameterTree } from "./ParameterTree";
import { PlottingPage } from "./PlottingPage";

/**
 * A class for handling User Client Config. Note that the conf is readonly, so this class should be a
 * variable that gets replaced, rather than modified. Although this idea may not work for editing confs.. so TBC!!
 */
class ParameterSetConf {
    public constructor(public readonly conf: UserClientConf, private mainSite: MainSite) { }

    public static parseFromJson(json: string, mainSite: MainSite): ParameterSetConf {
        let parsed;
        if (typeof(json) == 'string') {
            parsed = JSON.parse(json);
        } else {
            parsed = null;
        }
        let conf = ParameterSetConf.autoUpgradeUserClientConf(parsed, mainSite);
        // console.info('ParameterSetConf.parseFromJson()', { json: json, parsed: parsed, conf: conf });
        return new ParameterSetConf(conf, mainSite);
    }

    //: ideas: create methods .save() to store config to database, .deleteParamSet() to remove single param set, and also maybe methods to fetch latest config from server?


    /**
     * Helper method to reduce/flatten plot conf values that erroneously got stored in varying levels of string arrays.
     * NB: before this method was created, the existing data was manually verified and any user's ClientConfiguration
     * containing "__plot_confs" keys were checked - any values in string arrays are the same as the first array entry
     * if there are cases where multiple confs JSON strings got stored against a single axisId key...
     * See: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/flat#reduce_concat_isarray_recursivity
     * @param arr
     * @param d
     */
    private static flatDeep(arr, d = 1) {
        return d > 0 ? arr.reduce((acc, val) => acc.concat(Array.isArray(val) ? this.flatDeep(val, d - 1) : val), [])
            : arr.slice();
    };

    /**
     * Helper to try getting station details (mostly need stationId) from v1 & v2 confs 'siteId', as they used to
     * be built from the ParameterTree node label, which usually used the station label rather than ID, but from
     * third version of user client conf format (as of 2023-03), the parameter sets consistently used the actual
     * stationId from nodes rather than the label.
     * @param label
     * @param mainSite
     * @returns
     */
    private static matchLegacyStationLabel(label: string, mainSite: MainSite): Station | null {
        if (label in mainSite.staticDataManager.Schema.ParameterSchema.Stations) {
            return mainSite.staticDataManager.Schema.ParameterSchema.Stations[label].Station;
        }

        for (let stationId in mainSite.staticDataManager.Schema.ParameterSchema.Stations) {
            let _station = mainSite.staticDataManager.Schema.ParameterSchema.Stations[stationId].Station;
            if (_station.Label == label) {
                return _station;
            }
        }

        return null;
    }

    /**
     * Similar to `.matchLegacyStationLabel()` but to match the sensor label to sensorId. This will not work anymore if
     * sensors' DisplayName get changed to allow nested sensors in the tree. This however returns a string corresponding
     * to the sensorId (as API.ParameterSchemaSensor does not provide the sensorId!) or null if not matched.
     * @param stationId
     * @param label
     * @param mainSite
     * @returns
     */
    private static matchLegacySensorLabel(stationId: string, label: string, mainSite: MainSite): string | null {
        if (!(stationId in mainSite.staticDataManager.Schema.ParameterSchema.Stations)) { //: in case the stationId was not properly matched or even if it no longer is available in the ParameterSchema..
            return null;
        }

        let _sensors = mainSite.staticDataManager.Schema.ParameterSchema.Stations[stationId].Sensors;

        if (label in _sensors) {
            return label;
        }

        for (let sensorId in _sensors) {
            if (_sensors[sensorId].SensorDefinition && _sensors[sensorId].SensorDefinition.DisplayName == label) {
                //console.info('%cV2 matched sensor label', 'color: limegreen; font-style:italic', { label: label, sensorId: sensorId, stationId: stationId });
                return sensorId;
            } else {
                //console.info('%cV2 NOT-matched sensor label', 'color: blue; font-style:italic', { label: label, sensorId: sensorId, DisplayName: _sensors[sensorId].SensorDefinition?.DisplayName, stationId: stationId });
            }
        }

        return null;
    }

    private static confUpgradeFromV1(conf: Legacy.v1.ClientConfiguration, mainSite: MainSite): UserClientConf {
        let newConf: UserClientConf = {
            savedParameterSets: []
        };

        try {
            for (let paramSetId in conf.savedParameterSets) {
                let newParamSet: ParameterSets.ParameterSet = {
                    paramSetId: paramSetId,
                    ////paramSetsBySite: {}, // tmp.savedParameterSets[paramSetId],
                    plotConfigurations: {},
                    selectedParameters: []
                };

                for (let siteLabel in conf.savedParameterSets[paramSetId]) {
                    if (siteLabel == "__plot_confs") {
                        //: handle plot confs. This is a little bit messy, but the code that previously managed/stored plot configurations ended up storing
                        //: them as either string or string arrays, with sometimes multiple levels of arrays. See ParameterSetList.flatDeep() for information
                        //: on how old data is read to be converted to v2.
                        for (let axisIdAliasSensorId in conf.savedParameterSets[paramSetId][siteLabel]) {
                            let _conf: string | string[] = conf.savedParameterSets[paramSetId][siteLabel][axisIdAliasSensorId];
                            let _confStr: string;
                            let _confObj: ParameterSets.PlotAxisConfig; // API.SavedParameterSetV2PlotConfiguration;

                            if (Array.isArray(_conf)) {
                                _confStr = ParameterSetConf.flatDeep(_conf, Infinity)[0];
                            }

                            if (typeof (_conf) == "string") {
                                _confStr = _conf;
                            }

                            _confObj = JSON.parse(_confStr) as ParameterSets.PlotAxisConfig; // API.SavedParameterSetV2PlotConfiguration;

                            newParamSet.plotConfigurations[axisIdAliasSensorId] = _confObj;
                        }
                    } else {
                        //newParamSet.paramSetsBySite[siteLabel] = conf.savedParameterSets[paramSetId][siteLabel]; //: ignoring this as it's for older v2..

                        for (let sensorId in conf.savedParameterSets[paramSetId][siteLabel]) {
                            let parameters = conf.savedParameterSets[paramSetId][siteLabel][sensorId];

                            if (Array.isArray(parameters)) {
                                parameters.forEach(parameterId => {
                                    let stationId = ParameterSetConf.matchLegacyStationLabel(siteLabel, mainSite)?.Id ?? siteLabel;
                                    let selectedParam: ParameterSets.SelectedParameter = {
                                        isLegacy: true,
                                        stationId: stationId,
                                        sensorId: ParameterSetConf.matchLegacySensorLabel(stationId, sensorId, mainSite) ?? sensorId,
                                        parameterId: parameterId,
                                        variant: null //: variants weren't properly supported in v1!
                                    };

                                    newParamSet.selectedParameters.push(selectedParam);
                                });
                            } else {
                                console.warn(`ParamSets Legacy Convert ERROR: unable to convert v1 selected parameter as data is not in exected format (received ${typeof parameters}, when we expected 'string[]')`, { parameters: parameters, paramSetId: paramSetId, siteLabel: siteLabel, sensorId: sensorId, legacyConf: conf });
                            }
                        }
                    }
                }

                newConf.savedParameterSets.push(newParamSet);
            }

            return newConf;
        } catch (err) {
            console.warn("Error while parsing old API.ClientConfiguration..", { confReceived: conf, newConf: newConf, err: err });
        }
    }

    private static confUpgradeFromV2(conf: Legacy.v2.ClientConfigurationV2, mainSite: MainSite): UserClientConf {
        let newConf: UserClientConf = {
            savedParameterSets: []
        };

        if (conf == null) {
            return newConf;
        }

        try {
            if (!Array.isArray(conf.savedParameterSets)) {
                throw new Error('Invalid format of received conf.savedParameterSets !');
            }

            conf.savedParameterSets.forEach(paramSetV2 => {
                let newParamSet: ParameterSets.ParameterSet = {
                    paramSetId: paramSetV2.paramSetId,
                    plotConfigurations: paramSetV2.plotConfigurations, //: this has not changed from v2 to current (v3).
                    selectedParameters: []
                };

                for (let siteLabel in paramSetV2.paramSetsBySite) {
                    let _site = paramSetV2.paramSetsBySite[siteLabel];

                    for (let sensorId in _site) {
                        let _params = _site[sensorId];

                        let _stationId = ParameterSetConf.matchLegacyStationLabel(siteLabel, mainSite)?.Id ?? siteLabel;
                        let _sensorId = ParameterSetConf.matchLegacySensorLabel(_stationId, sensorId, mainSite); // ?? sensorId;
                        if (!_sensorId) {
                            _sensorId = sensorId;
                            console.warn('ParamSets v2 - unmatched sensor label:', sensorId, { stationId: _stationId, siteLabel: siteLabel, _site: _site, _station: ParameterSetConf.matchLegacyStationLabel(siteLabel, mainSite), sensorsList: mainSite.staticDataManager.Schema.ParameterSchema.Stations[_stationId]?.Sensors });
                        }

                        _params.forEach(_param => {
                            if (typeof _param == 'string') {
                                let selectedParam: ParameterSets.SelectedParameter = {
                                    isLegacy: true,
                                    stationId: _stationId,
                                    sensorId: _sensorId,
                                    parameterId: _param,
                                    variant: null
                                };

                                newParamSet.selectedParameters.push(selectedParam);
                            } else if (Array.isArray(_param)) { //: incorrectly typed, but JSON shows that sometimes this is a string[] to support variants?
                                (<string[]>_param).forEach(_variant => { //: broad assumptions here based on what I saw in JSON..
                                    let selectedParam: ParameterSets.SelectedParameter = {
                                        isLegacy: true,
                                        stationId: _stationId,
                                        sensorId: _sensorId,
                                        parameterId: _param,
                                        variant: _variant
                                    };

                                    newParamSet.selectedParameters.push(selectedParam);
                                });
                            }
                        });
                    }
                }

                newConf.savedParameterSets.push(newParamSet);
            });

            // console.info('ParameterSetConf.confUpgradeFromV2()', { conf: conf, newConf: newConf });
            return newConf;
        } catch (err) {
            console.warn("Error while parsing old API.ClientConfigurationV2..", { confReceived: conf, newConf: newConf, err: err });
        }
    }

    private static autoUpgradeUserClientConf(conf: Legacy.v1.ClientConfiguration | Legacy.v2.ClientConfigurationV2 | UserClientConf, mainSite: MainSite): UserClientConf {
        if (conf == null) {
            let _new: UserClientConf = {
                savedParameterSets: []
            };
            return _new;
        }

        if ($.isPlainObject(conf.savedParameterSets) && !Array.isArray(conf.savedParameterSets)) {
            return ParameterSetConf.confUpgradeFromV1(<Legacy.v1.ClientConfiguration>conf, mainSite);
        }

        if (Array.isArray(conf.savedParameterSets)) {
            let v2ParamSetsBySites = (<Legacy.v2.ClientConfigurationV2>conf).savedParameterSets.filter(set => $.isPlainObject(set.paramSetsBySite) && Object.keys(set.paramSetsBySite).length > 0);
            if (v2ParamSetsBySites.length > 0) {
                return ParameterSetConf.confUpgradeFromV2(<Legacy.v2.ClientConfigurationV2>conf, mainSite);
            }
        }

        if (Array.isArray(conf.savedParameterSets)) {
            let v3ParamSetsBySites = (<UserClientConf>conf).savedParameterSets.filter(set => 'selectedParameters' in set && Array.isArray(set.selectedParameters));
            if (v3ParamSetsBySites.length > 0) {
                return <UserClientConf>conf;
            }
        }
    }
}

export class ParameterSetList {
    private innerJQuery: JQuery;
    private heading: JQuery;
    private saveModal: JQuery;
    private saveModalListContent: JQuery;
    private paramsetLabel: JQuery;
    private saveModalValidationAlert: JQuery;
    private deleteModal: JQuery;
    private deleteModalBody: JQuery;
    private deleteKey: string;
    public loadedParamSetName: string = "";
    public loadedParamSetValues: ParameterSets.ParameterSet;
    private $btnSaveOrder: JQuery;
    private $paramSetsUL: JQuery;
    private $resizeHandle: JQuery;

    constructor(private mainSite: MainSite, private parameterTree: ParameterTree, private $container: JQuery, private plottingPage: PlottingPage) {
        let paramsTitleContainer = $('<div class="midi_PlottingPage_series_panel_sideContainer_parametersTitle clearfix flex-col-item--no-grow"></div>')
            .css("paddingTop", "0")
            .appendTo($container);

        $('<h5></h5>')
            .text("Parameter Sets")
            .appendTo(paramsTitleContainer);

        this.$btnSaveOrder = $('<div class="btn btn-default"><i class="fa fa-floppy-o"></i>&nbsp;Save Order</div>')
            .attr("disabled", "disabled")
            .hide()
            .on("click", (e) => {
                this.saveParamSetsOrder(mainSite);
                this.$btnSaveOrder.blur().attr("disabled", "disabled").hide();
            })
            .appendTo(paramsTitleContainer);

        this.innerJQuery = $('<div class="midi_PlottingPage_series_panel_sideContainer_box midi_PlottingPage_series_panel_sideContainer_parameterSets overflow-auto flex-col-item--grow"></div>')
            .appendTo(this.$container);

        this.saveModal = $('<div class="modal fade"></div>').appendTo('body');
        let modalContent = $('<div class="modal-content"></div>')
            .appendTo($('<div class="modal-dialog"></div>').appendTo(this.saveModal));
        
        let modalHeader = $('<div class="modal-header"></div>')
            .appendTo(modalContent);
        $('<button type="button" class="close" data-dismiss="modal" aria-hidden="true">&times;</button>')
            .appendTo(modalHeader);
        $('<h4 class="modal-title"></h4>').text('Save parameter set')
            .appendTo(modalHeader);

        let modalBody = $('<div class="modal-body" id="midi_PlottingPage_modal_saveParamSet_body"></div>')
            .appendTo(modalContent);

        let gotSomeParamSets = false;

        if (mainSite.currentUser.ClientConfiguration) {
            //check that we have parameter sets that we are also displaying sites in the tree for (i.e. parameter sets
            //that are relevant for the current region)
            let clientConfig: UserClientConf = this.getUserClientConfig(mainSite);
            // console.info('paramsetslist - clientConfig in constructor', { json: mainSite.currentUser.ClientConfiguration, parsed: clientConfig });
            if (clientConfig && Array.isArray(clientConfig.savedParameterSets)) {
                clientConfig.savedParameterSets.forEach(ps => {
                    if (!gotSomeParamSets && ps.selectedParameters.length > 0) {
                        gotSomeParamSets = true;
                    }

                    /*for (let siteLabel in ps.paramSetsBySite) {
                        let siteNode = this.parameterTree.fancyTree.rootNode.findFirst(siteLabel);
                        if (siteNode) {
                            gotSomeParamSets = true;
                        }
                    }*/
                });
            }
        }

        if (gotSomeParamSets) {
            $('<p></p>')
                .text("Click on an existing parameter set to overwrite it or enter a new parameter set name in the box below.")
                .appendTo(modalBody);
        }
        else {
            $('<p></p>')
                .text("Please enter a name for this parameter set in the box below.")
                .appendTo(modalBody);
        }

        this.saveModalListContent = $('<ul></ul>')
            .appendTo(modalBody);

        if (!gotSomeParamSets) {
            this.saveModalListContent.hide();
        }
        else {
            this.saveModalListContent.show();
        }

        this.paramsetLabel = $('<input class="form-control" type="text">')
            .appendTo(modalBody);

        this.saveModalValidationAlert = $('<div class="alert alert-danger"></div>')
            .text("You must enter a name for the parameter set")
            .appendTo(modalBody)
            .hide();

        var modalFooter = $('<div class="modal-footer"></div>').appendTo(modalContent);
        $('<button type="button" class="btn btn-default" data-dismiss="modal">Close</button>')
            .click(() => {
                this.saveModal.modal('hide');
            })
            .appendTo(modalFooter);

        $('<button type="button" class="btn btn-success">Save</button>')
            .appendTo(modalFooter)
            .click(() => {
                let setName = this.validate();
                //TODO: get the error colouring to work on the input
                if (setName === null) {
                    this.paramsetLabel.addClass("has-error");
                    this.saveModalValidationAlert.show();
                    return;
                }

                this.paramsetLabel.removeClass("has-error");
                this.saveModalValidationAlert.hide();

                let newParamSet: ParameterSets.ParameterSet = {
                    paramSetId: setName,
                    selectedParameters: this.getUISelectedParams(),
                    plotConfigurations: this.plottingPage.customLimitsByAxisLabel
                };

                let newConf: UserClientConf = this.getUserClientConfig(mainSite);
                if (!newConf) {
                    newConf = {
                        savedParameterSets: []
                    };
                }

                let psi: number = null;
                if (newConf) { //: check if we already have this paramSetId, and if so replace it, otherwise push.
                    psi = this.clientConfigParamSetFindIndex(setName, newConf);
                    if (psi >= 0) {
                        newConf.savedParameterSets[psi] = newParamSet;
                    } else {
                        newConf.savedParameterSets.push(newParamSet);
                    }
                }

                this.tryUpdateClientConfiguration(mainSite.currentUser.Id, JSON.stringify(newConf), this.paramsetLabel.val());
            });

        this.deleteModal = $('<div class="modal fade"></div>')
            .appendTo('body');
        modalContent = $('<div class="modal-content"></div>')
            .appendTo($('<div class="modal-dialog"></div>').appendTo(this.deleteModal));

        modalHeader = $('<div class="modal-header"></div>')
            .appendTo(modalContent);
        $('<button type="button" class="close" data-dismiss="modal" aria-hidden="true">&times;</button>')
            .appendTo(modalHeader);
        $('<h4 class="modal-title"></h4>').text('Delete parameter set')
            .appendTo(modalHeader);

        this.deleteModalBody = $('<div class="modal-body"></div>')
            .appendTo(modalContent);
        
        var modalFooter = $('<div class="modal-footer"></div>').appendTo(modalContent);
        $('<button type="button" class="btn btn-default" data-dismiss="modal">Close</button>')
            .click(() => {
                this.deleteKey = null;
                this.deleteModal.modal('hide');
            })
            .appendTo(modalFooter);
        $('<button type="button" class="btn btn-danger">Delete</button>')
            .appendTo(modalFooter)
            .click(() => {
                let conf: UserClientConf = this.getUserClientConfig(mainSite);
                if (!conf) {
                    conf = {
                        savedParameterSets: []
                    };
                }

                let psi: number = null;
                if (conf) {
                    psi = this.clientConfigParamSetFindIndex(this.deleteKey, conf);
                    if (psi >= 0) {
                        conf.savedParameterSets.splice(psi, 1);
                    }
                }

                this.tryUpdateClientConfiguration(mainSite.currentUser.Id, JSON.stringify(conf), null);
            });

        this.rebuildLists(this.getUserClientConfig(mainSite));
    }

    private tryUpdateClientConfiguration(email: string, clientConfiguration: string, autoSelectParamSetId: string): void {
        updateClientConfiguration(email, clientConfiguration, (err: RESTError, data: string) => {
            if (err) {
                this.saveModalValidationAlert.text('Error updating client configuration: ' + err.Message).show();
                return;
            }
            this.saveModal.modal('hide');
            this.deleteModal.modal('hide');
            this.mainSite.currentUser.ClientConfiguration = data;
            this.rebuildLists(this.getUserClientConfig(this.mainSite));
            if (autoSelectParamSetId) {
                this.innerJQuery.find('ul').find('li:contains("' + autoSelectParamSetId + '")').click();
            }
        });
    }

    private validate(): string {
        let setName = (this.paramsetLabel.val() as string).trim();
        return setName !== "" && setName !== null ? setName: null;
    }

    public rebuildLists(clientConfig: UserClientConf): void {
        this.innerJQuery.empty();
        this.saveModalListContent.empty();

        let noneli = $('<li></li>')
            .click(() => {
                this.deselectAllTreeNodes();
                this.paramsetLabel.val("");
                this.$paramSetsUL.find($(".selected")).removeClass("selected");
                noneli.addClass("selected");
                this.loadedParamSetName = "";
                this.loadedParamSetValues = null;
                this.plottingPage.resetCustomLimits();
            })
            .addClass("selected no-drag")
            .append($('<span></span>').text("None"));

        if (this.$paramSetsUL) {
            this.$paramSetsUL.remove();
        }

        this.$paramSetsUL = $('<ul></ul>')
            .append(noneli)
            .appendTo(this.innerJQuery);

        let currentParamsets: ParameterSets.ParameterSet[];

        if (clientConfig && Array.isArray(clientConfig.savedParameterSets)) {
            currentParamsets = clientConfig.savedParameterSets;
            // console.info('paramsets list - current config', clientConfig);

            currentParamsets.forEach((paramSet, i) => {
                let key: string = paramSet.paramSetId;
                let shouldIncludeInList: boolean = false;

                /*for (let siteId in paramSet.paramSetsBySite) {
                    let siteNode = this.parameterTree.fancyTree.rootNode.findFirst(siteId);
                    if (siteNode) {
                        shouldIncludeInList = true;
                    }
                }*/

                let paramSetSiteIds = paramSet.selectedParameters.map(_param => _param.stationId);
                // console.info('paramset list of site IDS in user param sets:', paramSetSiteIds);
                let treeNodes: any[] = this.parameterTree.flattenSensorNodesRecursive(null, false);
                paramSetSiteIds.forEach(_siteId => {
                    if (!shouldIncludeInList) {
                        let _filter = treeNodes.filter(x => x.stationId == _siteId || (x.NODE_TYPE == 'station' && x.title == _siteId));
                        if (_filter.length > 0) {
                            shouldIncludeInList = true;
                        }
                    }
                });

                if (!shouldIncludeInList) {
                    return;
                }

                let li = $('<li></li>')
                    .addClass("param-sets-item") //NB: this is used to retrieve order of items, so make sure that if you change this, it gets changed in the order retrieving function too!
                    .data("paramSetKey", key)
                    .data("paramSetData", paramSet)
                    .append($('<span></span>').text(key))
                    .append($('<i class="fa fa-trash-o"></i>')
                        .click((e) => {
                            this.deleteKey = key;
                            this.deleteModalBody.empty();
                            this.deleteModalBody
                                .append(
                                    $('<p></p>')
                                        .text("Are you sure you would like to delete the parameter set ")
                                        .append($('<strong></strong>').text(key))
                                        .append($('<span></span>').text("?"))
                                );

                            this.deleteModal.modal('show');
                            e.stopPropagation();
                        })
                    )
                    .appendTo(this.$paramSetsUL)
                    .click(() => {
                        this.deselectAllTreeNodes();

                        /*for (let siteId in paramSet.paramSetsBySite) {
                            let siteNode = this.parameterTree.fancyTree.rootNode.findFirst(siteId);
                            if (siteNode) {
                                siteNode.setExpanded();
                                for (let sensorId in paramSet.paramSetsBySite[siteId]) {
                                    let sensorNode = siteNode.findFirst((node) => {
                                        return node.title === sensorId;
                                    });
                                    if (sensorNode) {
                                        sensorNode.setExpanded();
                                        paramSet.paramSetsBySite[siteId][sensorId].forEach((param) => {
                                            let paramNode = sensorNode.findFirst(param);
                                            if (paramNode) {
                                                paramNode.setSelected();
                                            }
                                        });
                                    }
                                }
                            }
                            this.$paramSetsUL.find($(".selected")).removeClass("selected");
                            li.addClass("selected");

                            this.loadedParamSetName = key;
                            this.loadedParamSetValues = paramSet;
                            /// this.plottingPage.resetCustomLimits("__plot_confs" in this.loadedParamSetValues ? this.loadedParamSetValues['__plot_confs'] : null);
                            this.plottingPage.resetCustomLimits(paramSet.plotConfigurations);
                        }*/

                        paramSet.selectedParameters.forEach(param => {
                            let key = `${param.stationId}|${param.sensorId}|${param.parameterId}`;
                            if (param.variant) {
                                key += `|${param.variant}`;
                            }

                            let node = this.parameterTree.fancyTree.getNodeByKey(key);
                            if (node) {
                                node.setSelected();
                                node.setActive();
                                node.setFocus();
                            } else {
                                console.warn(`Unable to locate tree node for Parameter Set '${paramSet.paramSetId}' and selected parameter key: '${key}'.`, { key: key, param: param, set: paramSet, clientConfig: clientConfig });
                            }
                        });

                        this.$paramSetsUL.find($(".selected")).removeClass("selected");
                        li.addClass("selected");

                        this.loadedParamSetName = key;
                        this.loadedParamSetValues = paramSet;
                        /// this.plottingPage.resetCustomLimits("__plot_confs" in this.loadedParamSetValues ? this.loadedParamSetValues['__plot_confs'] : null);
                        this.plottingPage.resetCustomLimits(paramSet.plotConfigurations);


                        this.paramsetLabel.val(key);
                    });

                let modalLi = $('<li></li>')
                    .append($('<span></span>').text(key))
                    .appendTo(this.saveModalListContent)
                    .click(() => {
                        this.paramsetLabel.val(key);
                    });
            });
        }

        if (currentParamsets) {
            this.$paramSetsUL.sortable({
                cancel: ".no-drag",
                placeholder: "param-set-drag-placeholder selected",
                forcePlaceholderSize: true,
                axis: "y",
                containment: "parent",
                opacity: 0.35,
                update: (e, ui) => {
                    this.$btnSaveOrder.removeAttr("disabled").show();
                }
            });

            this.$container.css({ display: '' }); //: NB: not using .show() as it would set "display: 'block'" which prevents the flew display to work properly!
            this.plottingPage.$panelRightTop.css({ display: '' }); //: NB: not using .show() as it would set "display: 'block'" which prevents the flew display to work properly!
            this.plottingPage.$panelRightTop.next('.plot-page--splitter').css({ display: '' });
        } else {
            this.$container.hide();
            this.plottingPage.$panelRightTop.hide();
            this.plottingPage.$panelRightTop.next('.plot-page--splitter').hide();
        }
    }

    private deselectAllTreeNodes(): void {
        this.parameterTree.fancyTree.getSelectedNodes().forEach((n) => {
            n.setSelected(false);
            n.visitParents((p) => {
                p.setExpanded(false, { noAnimation: true });
            });
        });
    }

    private getUISelectedParams(): ParameterSets.SelectedParameter[] {
        let result: ParameterSets.SelectedParameter[] = [];

        this.parameterTree.fancyTree.getSelectedNodes().forEach(node => {
            try {
                let selected: ParameterSets.SelectedParameter = {
                    stationId: node.data.stationId,
                    sensorId: node.data.sensorId,
                    parameterId: node.data.parameterId,
                    variant: node.data.NODE_TYPE == 'variant' ? (node.data.variantId ?? 'null') : null //: NB: need string 'null' to use in the tree nodes keys search!
                };

                result.push(selected);
            } catch (err) {
                console.warn('Param Sets List: ERROR while trying to retrieve list of selected parameters from UI.', err);
            }
        });

        return result;
    }

    public getSaveButton(): JQuery {
        return $('<div class="btn btn-default"><i class="fa fa-floppy-o"></i>&nbsp;Save Set</div>')
            .click(() => {
                let _selected = this.getUISelectedParams();
                if (_selected.length == 0) {
                    alert("Please select parameters");
                    return;
                }

                this.saveModal.modal('show');
            });
    }

    public getLoadedParamSetName(): string {
        return this.loadedParamSetName;
    }

    public getLoadedParamSetValues(): ParameterSets.ParameterSet {
        return this.loadedParamSetValues;
    }

    public getListElement(): JQuery {
        return this.innerJQuery;
    }

    /**
     * This method will retrieve the user's client config string loaded in the MainsSite instance, and pass it to parseUserClientConf to return
     * appropriate object. This is a synchronous version that can only work with loaded objects in instance.
     * @param mainSite The instance of the MainSite (required for this synchronous version)
     */
    public getUserClientConfig(mainSite: MainSite): UserClientConf {
        return ParameterSetConf.parseFromJson(mainSite.currentUser.ClientConfiguration, mainSite).conf;
    }

    /**
     * A helper function to find a Client Configuration Parameter Set (v2) by its ID. Returns
     * the full Param Set if matched, otherwise null.
     * @param paramSetId The ID for the set we're looking for
     * @param config The ClientConfiguration, parsed. If null, and we've got an instance of this.mainSite, then this method will fetch the client config automatically.
     */
    public clientConfigParamSetFind(paramSetId: string, config: UserClientConf = null): ParameterSets.ParameterSet {
        if (config == null && this.mainSite) {
            config = this.getUserClientConfig(this.mainSite);
        }

        if (config !== null) {
            let filter: ParameterSets.ParameterSet[] = config.savedParameterSets.filter(ps => ps.paramSetId == paramSetId);
            if (filter.length == 1) {
                return filter[0];
            } else {
                return null;
            }
        }

        return null;
    }

    /**
     * Utilty method to get the index position of a given Param Set by its ID (for replacement/deletion for example). If not found, returns -1.
     * @param paramSetId
     * @param config
     * @param enforceSingleMatch If set to TRUE and we find more than 1 matching paramSetId, then an error is raised.
     */
    public clientConfigParamSetFindIndex(paramSetId: string, config: UserClientConf = null, enforceSingleMatch: boolean = true): number {
        if (config == null && this.mainSite) {
            config = this.getUserClientConfig(this.mainSite);
        }

        let psIndex: number = -1; //: NB: this only gets the FIRST match. If for some weird reason there should be conflictive ParamSets with same ID (maybe other region?) then this could become inprecise.
        let psMatchCount: number = 0;

        if (config !== null) {
            config.savedParameterSets.forEach((ps, i) => {
                if (ps.paramSetId == paramSetId) {
                    psIndex = i;
                    psMatchCount++;
                }
            });
        }

        if (enforceSingleMatch && psMatchCount > 1) {
            throw new Error(`There are ${psMatchCount} Parameter Sets matching ID '${paramSetId}', when only 1 is allowed.`);
        }

        return psIndex;
    }

    /**
     * This method will remove the specified Parameter Set by its ID, and return an Array of Parameter Sets excluding the one removed.
     * NB: this only returns a modified array, and does not affect any variables or properties of the current instances!
     * @param paramSetId
     * @param config
     */
    public clientConfigParamSetRemove(paramSetId: string, config: UserClientConf = null): ParameterSets.ParameterSet[] {
        let result: ParameterSets.ParameterSet[] = [];

        if (config == null && this.mainSite) {
            config = this.getUserClientConfig(this.mainSite);
        }

        if (config !== null) {
            let psIndex: number = this.clientConfigParamSetFindIndex(paramSetId, config);

            if (psIndex >= 0) {
                config.savedParameterSets.splice(psIndex, 1);
            }

            return config.savedParameterSets;
        }

        return result;
    }

    /**
     * Retrieve the order from the UI list of paramSets, and build a new Client Configuration to send to database for update.
     * @param mainSite
     */
    private saveParamSetsOrder(mainSite: MainSite): void {
        let clientConf = this.getUserClientConfig(mainSite);
        let _log: string[] = [];

        let paramSetIdsOrdered: string[] = [];
        if (this.$paramSetsUL) {
            this.$paramSetsUL.find("li.param-sets-item").each((i, li) => {
                paramSetIdsOrdered.push($(li).data("paramSetKey"));
            });

            _log.push(`Retrieved order of paramsets defined by user.. ${paramSetIdsOrdered.join(',')}`);
        }

        let newConf: UserClientConf = {
            savedParameterSets: []
        };

        //: first loop through ordered list, then loop over existing conf to add key/conf combinations not listed in UI (perhaps due to region or other reason..)
        paramSetIdsOrdered.forEach((paramSetId, i) => {
            let ps: ParameterSets.ParameterSet = this.clientConfigParamSetFind(paramSetId, clientConf);
            if (ps) {
                newConf.savedParameterSets.push(ps);
                _log.push(`Add ps (ordered): ${ps.paramSetId}`);
            } else {
                _log.push(`PS not found (ordered): ${paramSetId}`);
            }
        });

        clientConf.savedParameterSets.forEach((ps, i) => {
            if (this.clientConfigParamSetFind(ps.paramSetId, newConf) == null) {
                newConf.savedParameterSets.push(ps);
            }
        });

        let paramSetIdsOrderedPostUpdate: string[] = [];
        newConf.savedParameterSets.forEach((ps, i) => {
            paramSetIdsOrderedPostUpdate.push(ps.paramSetId);
        });

        // console.warn("UPDATE CLIENT CONFIG...", { log: _log, user: mainSite.currentUser.Id, paramSetIdsOrdered: paramSetIdsOrdered, paramSetIdsOrderedPostUpdate: paramSetIdsOrderedPostUpdate, conf_original: clientConf, conf_original_str: JSON.stringify(clientConf), conf_new: newConf, conf_new_str: JSON.stringify(newConf), _this: this });
        this.tryUpdateClientConfiguration(mainSite.currentUser.Id, JSON.stringify(newConf), this.paramsetLabel.val());
    }
}